import { useState } from 'react'
import { Calendar, Clock, Zap, Users, CheckCircle2, BarChart3, Bell, ArrowRight } from 'lucide-react'

export default function CalendarPage() {
  const [stats] = useState({
    upcomingEvents: 234,
    teamMembers: 156,
    integrations: 5,
    timeSync: '100%',
  })

  const features = [
    { title: '📅 Shared Calendar', description: 'Team calendar with availability management', icon: Calendar, color: 'from-blue-600 to-blue-500', stats: '234 Events' },
    { title: '⏰ Scheduling', description: 'Smart scheduling with conflict detection', icon: Clock, color: 'from-green-600 to-green-500', stats: 'Real-time Check' },
    { title: '⚡ Reminders', description: 'Automatic reminders before meetings', icon: Bell, color: 'from-yellow-600 to-yellow-500', stats: '100% Coverage' },
    { title: '👥 Team Availability', description: 'Find available meeting times instantly', icon: Users, color: 'from-purple-600 to-purple-500', stats: 'AI Optimized' },
    { title: '✓ Booking Confirmation', description: 'Automatic confirmations and updates', icon: CheckCircle2, color: 'from-teal-600 to-teal-500', stats: 'Auto-Sent' },
    { title: '📊 Calendar Analytics', description: 'Insights on team time and productivity', icon: BarChart3, color: 'from-cyan-600 to-cyan-500', stats: 'Real-time' },
    { title: '🔗 Integration Hub', description: 'Connect Google, Outlook, Zoom, Teams', icon: Zap, color: 'from-indigo-600 to-indigo-500', stats: '5+ Apps' },
    { title: '🌍 Timezone Support', description: 'Automatic timezone conversion', icon: Clock, color: 'from-orange-600 to-orange-500', stats: 'All Zones' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-8 text-white shadow-2xl border border-purple-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Calendar className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Calendar & Scheduling</h1>
                </div>
                <p className="text-purple-100 text-lg font-semibold">Smart Team Calendar & Meeting Management</p>
                <p className="text-purple-200 text-sm mt-1">Google Calendar • Outlook • Teams • Zoom Integration</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.upcomingEvents}</div>
                <div className="text-sm text-purple-100">Upcoming Events</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Upcoming Events', value: stats.upcomingEvents, icon: Calendar, color: 'from-purple-600 to-purple-500' },
            { label: 'Team Members', value: stats.teamMembers, icon: Users, color: 'from-pink-600 to-pink-500' },
            { label: 'Integrations', value: stats.integrations, icon: Zap, color: 'from-blue-600 to-blue-500' },
            { label: 'Sync Status', value: stats.timeSync, icon: CheckCircle2, color: 'from-green-600 to-green-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Calendar Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div key={idx} className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1">
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
